import { OrderLineTypes } from "@repo/database";
import { groupBy } from "@repo/utils";
import Decimal from "decimal.js";
import { create } from "zustand";
import { devtools } from "zustand/middleware";

import type { CartItem } from "~/providers/cart-provider";

interface CourseState {
  items: CartItem[];
  miscProductOpen: boolean;
  orderContextOpen: boolean;
  orderDiscountDialogOpen: boolean;
  orderCommentDialogOpen: boolean;
  searchInput: string;
  selectedItem?: CartItem;
  cartItemContextOpen: boolean;
  extraDialogOpen: boolean;
  noTableSelectedDialogOpen: boolean;
  setNoTableSelectedDialogOpen: (open: boolean) => void;
  setExtraDialogOpen: (open: boolean) => void;
  setCartItemContextOpen: (open: boolean) => void;
  setOrderContextOpen: (open: boolean) => void;
  setSelectedItem: (item?: CartItem) => void;
  setSearchInput: (input: string) => void;
  setOrderCommentDialogOpen: (open: boolean) => void;
  setOrderDiscountDialogOpen: (open: boolean) => void;
  setMiscProductOpen: (open: boolean) => void;
  getNewItems: () => CartItem[];
  getTotal: () => number;
  addItem: (item: CartItem) => void;
  removeItem: (item: CartItem, amount?: number) => void;
  setItems: (items: CartItem[]) => void;
  reset: () => void;
  clear: () => void;
}

export const useCartStore = create<CourseState>()(
  devtools((set, get) => ({
    items: [],
    miscProductOpen: false,
    extraDialogOpen: false,
    cartItemContextOpen: false,
    orderContextOpen: false,
    orderDiscountDialogOpen: false,
    orderCommentDialogOpen: false,
    noTableSelectedDialogOpen: false,
    searchInput: "",
    setNoTableSelectedDialogOpen(open) {
      set({ noTableSelectedDialogOpen: open });
    },
    setCartItemContextOpen(open: boolean) {
      set({ cartItemContextOpen: open });
    },
    setExtraDialogOpen: (open: boolean) => {
      set({ extraDialogOpen: open });
    },
    setOrderContextOpen(open: boolean) {
      set({ orderContextOpen: open });
    },
    setSelectedItem: (item: CartItem | undefined) => {
      set({ selectedItem: item });
    },
    setSearchInput: (input: string) => {
      set({ searchInput: input });
    },

    setOrderCommentDialogOpen: (open: boolean) => {
      set({ orderCommentDialogOpen: open });
    },
    setOrderDiscountDialogOpen: (open: boolean) => {
      set({ orderDiscountDialogOpen: open });
    },
    setMiscProductOpen: (open: boolean) => {
      set({ miscProductOpen: open });
    },
    getNewItems: () => {
      return get().items.filter((item) => !item.ordered);
    },
    getTotal: () => {
      const total = get()
        .items.filter((item) => item.type !== OrderLineTypes.discount)
        .reduce((acc, item) => acc + item.value * item.quantity, 0);

      const discount = get()
        .items.filter((item) => item.type === OrderLineTypes.discount)
        .reduce((acc, item) => acc + item.value * item.quantity, 0);

      return new Decimal(total - discount).toDecimalPlaces(2).toNumber();
    },
    addItem: (item: CartItem) => {
      set((state) => ({
        items: [
          ...state.items.filter((i) => i.reference !== item.reference),
          item,
        ],
      }));
    },
    clear: () => {
      set({ items: [] });
    },
    reset: () => {
      set((state) => ({
        items: state.items.filter((item) => item.ordered),
      }));
    },

    setItems: (items: CartItem[]) => {
      set((state) => {
        // Check if we're dealing with the same cart or a completely different one
        const isSameCart = items.some((newItem) =>
          state.items.some(
            (existingItem) => existingItem.orderId === newItem.orderId,
          ),
        );

        // If it's not the same cart, completely replace all items
        if (!isSameCart) {
          return {
            items: [...items, ...state.items.filter((item) => !item.ordered)],
          };
        }

        // Keep existing ordered items that aren't in the new items list
        const existingOrderedItems = state.items.filter(
          (existingItem) =>
            existingItem.ordered &&
            !items.some(
              (newItem) => newItem.reference === existingItem.reference,
            ),
        );

        // Keep all existing unordered items
        const existingUnorderedItems = state.items.filter(
          (item) => !item.ordered,
        );

        // Filter out new items that have the same reference as existing unordered items
        const filteredNewItems = items.filter(
          (newItem) =>
            !existingUnorderedItems.some(
              (existingItem) =>
                existingItem.reference === newItem.reference &&
                existingItem.quantity >= 1,
            ),
        );

        console.log(existingOrderedItems);
        console.log(existingUnorderedItems);
        console.log(filteredNewItems);

        // Combine everything: existing ordered items (that aren't being updated),
        // all existing unordered items, and filtered new items
        return {
          items: [
            ...existingOrderedItems,
            ...existingUnorderedItems,
            ...filteredNewItems,
          ],
        };
      });
    },

    removeItem: (item: CartItem, amount?: number) => {
      set((state) => {
        const groupedItems = groupBy(state.items, "reference");
        const orderedItemsToRemove = Object.values(groupedItems)
          .filter(
            (group) =>
              group.some(
                (i) =>
                  i.ordered === true &&
                  (i.reference === item.reference ||
                    i.parentReference === item.reference),
              ) && group.reduce((sum, i) => sum + (i.quantity || 0), 0) > 0,
          )
          .flatMap((group) => {
            const quantityToRemove = amount ?? 1; // Use amount if provided, otherwise default to 1

            return group.map((item) => {
              const newQuantity = -quantityToRemove; //  Make sure it's negative for removal

              return {
                ...item,
                quantity: newQuantity,
                ordered: false,
                printed: false,
                createdAt: new Date(),
              };
            });
          });

        const unOrderedItemsToRemove = state.items.filter(
          (i) =>
            i.ordered === false &&
            (i.reference === item.reference ||
              i.parentReference === item.reference),
        );

        return {
          items: state.items
            .concat(orderedItemsToRemove)
            .filter(
              (i) =>
                !unOrderedItemsToRemove.some(
                  (unOrderedItem) => unOrderedItem.reference === i.reference,
                ),
            ),
        };
      });
    },
  })),
);
